import { useCallback, useEffect, useState } from 'react';
import { Button, Container, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import withAuth from '../utility/withAuth';
import { useAppDispatch } from '../store/hooks';
import { getValue, renderErrorToast, storeValue, handleSuccess } from '../utility/helpers';
import { PROFILE_STATUS } from '../enums/applicationEnums';
import { logo } from '../assets/images/images';
import { fetchPricingPlans, selectPlan, contactSales, signupPlanSelection, Plan } from '../services/pricingService';
import { getUserProfile } from '../store/features/auth';

const PricingPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const profileStatus = getValue('profileStatus');
  const isSignupMode = getValue('signupPlanMode') === 'true';

  // Initialize with cached tier data for immediate render
  const cachedTierData = getValue('tierData') ? JSON.parse(getValue('tierData') || '{}') : {};
  const [assignedTier, setAssignedTier] = useState<any>(cachedTierData);
  const [userDetails, setUserDetails] = useState<any>({});
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch plans from backend
  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const plans = await fetchPricingPlans();
      setPlans(plans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError(error instanceof Error ? error.message : 'Failed to load pricing plans. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch user profile to get current tier
  const fetchUserDetails = useCallback(async () => {
    try {
      const companyId = getValue('companyId');
      const payload = {
        companyId,
        loader: false
      };
      const res = await dispatch(getUserProfile(payload)).unwrap();
      if (res?.data?.tier_assigned) {
        storeValue('tierData', JSON.stringify(res?.data?.tier_assigned));
        setAssignedTier(res?.data?.tier_assigned);
      }
      // Store complete user details for contact sales
      setUserDetails(res?.data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    // Only fetch once on mount
    fetchPlans();
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigateTo = (route: string) => {
    navigate(route);
  };

  const handleSelectPlan = async (planId: number) => {
    try {
      if (isSignupMode) {
        const res = await signupPlanSelection(planId);
        // Clear signup mode after selection
        storeValue('signupPlanMode', '');
        if (res?.data?.redirect_to_signin) {
          // Clear auth and redirect to sign-in per flow
          localStorage.removeItem('authToken');
          const selectedPlan = plans.find(p => p.id === planId);
          const isPremium = selectedPlan ? !selectedPlan.is_free : false;
          const message = isPremium
            ? 'Thanks for your interest in the premium plan! We will get back to you soon regarding your upgrade. In the meantime, enjoy your Free account. You will be redirected to the sign-in page to access your account.'
            : 'Thank you for joining SBR2TH! You will be redirected to the sign-in page to access your account. Enjoy your Free account.';
          setSuccessMessage(message);
          handleSuccess(message);
          setTimeout(() => {
            navigateTo('/');
          }, 3000);
          return;
        }
      } else {
        await selectPlan(planId);
      }
      
      // Show success message
      const selectedPlan = plans.find(p => p.id === planId);
      if (selectedPlan && selectedPlan.is_free) {
        const msg = `Free tier assigned successfully. You can now create up to ${selectedPlan.no_of_job_creation} job posts and view up to ${selectedPlan.no_of_profile_views} candidate profiles.`;
        setSuccessMessage(msg);
        handleSuccess(msg);
      } else if (selectedPlan) {
        const msg = `${selectedPlan.name} plan selected successfully!`;
        setSuccessMessage(msg);
        handleSuccess(msg);
      } else {
        const msg = 'Plan selected successfully!';
        setSuccessMessage(msg);
        handleSuccess(msg);
      }

      if (!isSignupMode) {
        // Redirect after a delay or show success state
        setTimeout(() => {
          navigateTo('/dashboard');
        }, 3000);
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to select plan. Please try again later.';
      setError(errorMessage);
      renderErrorToast(errorMessage);
    }
  };

  const handleContactSales = async (planId: number) => {
    try {
      const requestedPlan = plans.find(p => p.id === planId);
      if (!requestedPlan) {
        throw new Error('Requested plan not found');
      }

      await contactSales(userDetails, assignedTier, requestedPlan);
      
      const msg = 'Thanks for your interest in the premium plan! We will get back to you soon regarding premium. In the meantime, enjoy the Free account.';
      setSuccessMessage(msg);
      handleSuccess(msg);
      
      // Redirect to dashboard after showing success message
      setTimeout(() => {
        navigateTo('/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Error contacting sales:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to contact sales. Please try again later.';
      setError(errorMessage);
      renderErrorToast(errorMessage);
    }
  };

  const handleRetry = () => {
    fetchPlans();
  };

  // Allow pricing in signup mode even if profile not completed
  if (!isSignupMode && profileStatus !== PROFILE_STATUS.COMPLETED) {
    return null;
  }

  return (
    <>
      <div className="logo-container">
        <img src={logo} onClick={() => navigateTo('/')} alt="logo" className='brand-logo' />
      </div>
      
      <Container className="pricing-container">
        <div className="pricing-header text-center mb-4">
          <h1 className="display-4 mb-2">Choose Your Plan<span className="text-green">.</span></h1>
          <p className="lead">Select the plan that best fits your hiring needs</p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <Alert 
            variant="success" 
            className="success-alert"
            dismissible
            onClose={() => setSuccessMessage(null)}
          >
            <i className="bi bi-check-circle-fill me-2"></i>
            {successMessage}
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert 
            variant="danger" 
            className="error-alert"
            dismissible
            onClose={() => setError(null)}
          >
            <i className="bi bi-exclamation-circle-fill me-2"></i>
            {error}
            <Button 
              variant="link" 
              className="p-0 ms-3 text-danger text-decoration-underline"
              onClick={handleRetry}
            >
              Try again
            </Button>
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading available plans...</p>
          </div>
        )}

        {/* No Plans Available */}
        {!loading && !error && plans.length === 0 && (
          <div className="text-center my-5">
            <h4>No pricing plans available at the moment.</h4>
            <p className="text-muted">Please contact support or try again later.</p>
            <Button 
              variant="outline-primary" 
              className="mt-3"
              onClick={handleRetry}
            >
              Refresh Plans
            </Button>
          </div>
        )}

        {/* Plans Display */}
        {!loading && !error && plans.length > 0 && (
          <div className="row justify-content-center">
            {plans.map((plan) => (
              <div key={plan.id} className="col-md-6 col-lg-4 mb-4">
                <div className={`card pricing-card h-100 ${plan.is_popular ? 'popular-plan' : ''} ${assignedTier?.id === plan.id ? 'current-plan' : ''}`}>
                  {plan.is_popular && (
                    <div className="popular-badge">Popular</div>
                  )}
                  {assignedTier?.id === plan.id && (
                    <div className="current-plan-badge">Current Plan</div>
                  )}
                  <div className="card-body">
                    <h5 className="card-title text-center">{plan.name}</h5>
                    <h2 className="price text-center text-primary mb-4">
                      {plan.price || ''}
                    </h2>
                    
                    <ul className="plan-features">
                      {(() => { const isAdvanced = (plan?.name || '').trim().toLowerCase() === 'sbr2th advanced'; return (
                        <>
                          <li>
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            {isAdvanced ? 'Unlimited Job Posts' : `${plan.no_of_job_creation} Job Posts`}
                          </li>
                          <li>
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            {isAdvanced ? 'Unlimited Profile Views' : `${plan.no_of_profile_views} Profile Views`}
                          </li>
                          <li>
                            {plan.no_of_downloads === 0 && !isAdvanced ? (
                              <i className="bi bi-x-circle-fill text-danger me-2"></i>
                            ) : (
                              <i className="bi bi-check-circle-fill text-success me-2"></i>
                            )}
                            {isAdvanced
                              ? 'Unlimited Downloads'
                              : (plan.no_of_downloads === 0 ? 'No Downloads' : `${plan.no_of_downloads} Downloads`)}
                          </li>
                        </>
                      ); })()}
                      <li>
                        {plan.is_free ? (
                          <>
                            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                            Candidate details masked
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                            Full candidate details
                          </>
                        )}
                      </li>
                      {plan.name === 'SBR2TH Custom Plan' && (
                        <li>
                          <i className="bi bi-check-circle-fill text-success me-2"></i>
                          Custom enterprise features
                        </li>
                      )}
                    </ul>
                  </div>
                  <div className="card-footer bg-transparent border-0 text-center pb-4">
                    <Button 
                      variant="primary"
                      className="upgrade-header-btn"
                      onClick={() => plan.is_free || isSignupMode ? handleSelectPlan(plan.id) : handleContactSales(plan.id)}
                      style={{
                        backgroundColor: '#3DCBB1',
                        border: 'none',
                        borderRadius: '50px',
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        minWidth: '120px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {isSignupMode ? 'SELECT PLAN' : (plan.is_free ? 'GET STARTED' : 'CONTACT SALES')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Container>
    </>
  );
};

export default withAuth(PricingPage);
